import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import './GenerationProgress.css';

// Fun facts to show during generation
const funFacts = [
    "💡 The human brain can process images in just 13 milliseconds",
    "🧠 Learning something new creates new neural pathways in your brain",
    "📚 Reading for just 6 minutes can reduce stress by 68%",
    "🎯 It takes about 66 days to form a new habit",
    "🚀 You remember 90% of what you teach to others",
    "💪 Short study sessions (25-30 min) are more effective than cramming",
    "🎮 Gamification can increase learning retention by up to 40%",
    "🌙 Sleep helps consolidate memories and learning",
    "✨ The best time to learn is when your brain is most alert",
    "🔄 Spaced repetition can increase retention by 200%"
];

function GenerationProgress({ queueId, topic, onComplete, onError }) {
    const [progress, setProgress] = useState({
        percent: 0,
        currentStep: 'Starting...',
        completed: 0,
        total: 1
    });
    const [status, setStatus] = useState('pending');
    const [funFact, setFunFact] = useState(funFacts[0]);
    const [learningPath, setLearningPath] = useState(null);
    const eventSourceRef = useRef(null);
    const navigate = useNavigate();

    // Rotate fun facts
    useEffect(() => {
        const interval = setInterval(() => {
            setFunFact(funFacts[Math.floor(Math.random() * funFacts.length)]);
        }, 5000);
        return () => clearInterval(interval);
    }, []);

    // Connect to SSE stream
    useEffect(() => {
        if (!queueId) return;

        const eventSource = new EventSource(`${API_URL}/api/generation/stream/${queueId}`);
        eventSourceRef.current = eventSource;

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                if (data.status === 'not_found') {
                    eventSource.close();
                    onError?.('Generation job not found');
                    return;
                }

                setStatus(data.status);
                setProgress(data.progress || progress);

                if (data.learningPath) {
                    setLearningPath(data.learningPath);
                }

                if (data.status === 'complete') {
                    eventSource.close();
                    onComplete?.(data);
                }

                if (data.status === 'failed') {
                    eventSource.close();
                    onError?.(data.errors?.[0]?.general || 'Generation failed');
                }
            } catch (err) {
                console.error('Error parsing SSE data:', err);
            }
        };

        eventSource.onerror = (err) => {
            console.error('SSE error:', err);
            eventSource.close();
            // Fall back to polling
            startPolling();
        };

        return () => {
            eventSource.close();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queueId]);

    // Polling fallback
    const startPolling = () => {
        const pollInterval = setInterval(async () => {
            try {
                const response = await fetch(`${API_URL}/api/generation/status/${queueId}`);
                const data = await response.json();

                setStatus(data.status);
                setProgress(data.progress || progress);

                if (data.learningPath) {
                    setLearningPath(data.learningPath);
                }

                if (data.status === 'complete' || data.status === 'failed') {
                    clearInterval(pollInterval);
                    if (data.status === 'complete') {
                        onComplete?.(data);
                    } else {
                        onError?.(data.errors?.[0]?.general || 'Generation failed');
                    }
                }
            } catch (err) {
                console.error('Polling error:', err);
            }
        }, 2000);

        return () => clearInterval(pollInterval);
    };

    const getStatusEmoji = () => {
        if (status === 'complete') return '🎉';
        if (status === 'failed') return '❌';
        if (progress.percent < 20) return '🚀';
        if (progress.percent < 50) return '📚';
        if (progress.percent < 80) return '✨';
        return '🎯';
    };

    const getStatusColor = () => {
        if (status === 'complete') return '#10b981';
        if (status === 'failed') return '#ef4444';
        return '#667eea';
    };

    return (
        <div className="generation-progress-container">
            <div className="generation-progress-card">
                {/* Header */}
                <div className="progress-header">
                    <span className="progress-emoji">{getStatusEmoji()}</span>
                    <h2>Building Your Learning Path</h2>
                    <p className="progress-topic">{topic}</p>
                </div>

                {/* Progress Bar */}
                <div className="progress-bar-container">
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{
                                width: `${progress.percent}%`,
                                backgroundColor: getStatusColor()
                            }}
                        />
                    </div>
                    <div className="progress-stats">
                        <span className="progress-percent">{progress.percent}%</span>
                        <span className="progress-count">
                            {progress.completed}/{progress.total} items
                        </span>
                    </div>
                </div>

                {/* Current Step */}
                <div className="current-step">
                    <div className="step-indicator">
                        {status !== 'complete' && status !== 'failed' && (
                            <div className="step-spinner"></div>
                        )}
                        <span>{progress.currentStep}</span>
                    </div>
                </div>

                {/* Learning Path Preview */}
                {learningPath && learningPath.levels && (
                    <div className="path-preview">
                        <h3>Your Curriculum</h3>
                        <div className="levels-preview">
                            {learningPath.levels.slice(0, 3).map((level, idx) => (
                                <div key={idx} className="level-preview-item">
                                    <span className="level-badge">L{level.levelNumber}</span>
                                    <span className="level-title">{level.title}</span>
                                    <span className="level-modules">
                                        {level.modules?.length || 0} lessons
                                    </span>
                                </div>
                            ))}
                            {learningPath.levels.length > 3 && (
                                <div className="level-preview-more">
                                    +{learningPath.levels.length - 3} more levels
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Fun Fact */}
                <div className="fun-fact">
                    <p>{funFact}</p>
                </div>

                {/* Actions */}
                {status === 'complete' && (
                    <button
                        className="btn btn-primary btn-large"
                        onClick={() => navigate('/learning-path', { state: { topic } })}
                    >
                        🚀 Start Learning
                    </button>
                )}

                {status === 'failed' && (
                    <button
                        className="btn btn-danger"
                        onClick={() => window.location.reload()}
                    >
                        Try Again
                    </button>
                )}
            </div>
        </div>
    );
}

export default GenerationProgress;
